import { useCallback, useEffect, useState } from 'react';
import type { FailureMode, Severity, ValidationIssue } from '../types';

export function Badge({
  tone = 'default',
  children,
}: {
  tone?: 'default' | 'ok' | 'warn' | 'danger' | 'info';
  children: React.ReactNode;
}) {
  return <span className={`badge ${tone === 'default' ? '' : tone}`}>{children}</span>;
}

const FAILURE_TONE: Record<FailureMode, 'ok' | 'warn' | 'danger' | 'default'> = {
  fail_closed: 'ok',
  fail_open_debt_blue: 'danger',
  fail_open_phantom: 'danger',
  fail_open_compensated: 'warn',
  not_applicable: 'default',
};

const FAILURE_LABEL: Record<FailureMode, string> = {
  fail_closed: 'fail closed',
  fail_open_debt_blue: 'falls back to Debt Blue',
  fail_open_phantom: 'phantom group key',
  fail_open_compensated: 'defaults, compensated',
  not_applicable: 'no partner routing',
};

export function FailureBadge({ mode }: { mode: FailureMode }) {
  return <Badge tone={FAILURE_TONE[mode]}>{FAILURE_LABEL[mode]}</Badge>;
}

export function Meter({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="meter" style={{ width: 70 }}>
        <span style={{ width: `${pct}%` }} />
      </span>
      <span className="small muted">{pct}%</span>
    </span>
  );
}

const SEVERITY_TONE: Record<Severity, 'danger' | 'warn' | 'info'> = {
  error: 'danger',
  warning: 'warn',
  advisory: 'info',
};

export function IssueList({ issues }: { issues: ValidationIssue[] }) {
  if (issues.length === 0) {
    return <div className="callout ok">No issues. This configuration is publishable.</div>;
  }
  return (
    <>
      {issues.map((issue, index) => (
        <div key={`${issue.code}-${index}`} className={`callout ${SEVERITY_TONE[issue.severity]}`}>
          <Badge tone={SEVERITY_TONE[issue.severity]}>{issue.severity}</Badge>{' '}
          <code>{issue.code}</code>
          <div style={{ marginTop: 4 }}>{issue.message}</div>
          {(issue.parent_key || issue.lambda_key) && (
            <div className="small muted" style={{ marginTop: 4 }}>
              {issue.parent_key && <>parent {issue.parent_key} </>}
              {issue.lambda_key && <>lambda {issue.lambda_key}</>}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export function Toast({
  message,
  tone,
  onDone,
}: {
  message: string;
  tone: 'info' | 'error';
  onDone: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDone, tone === 'error' ? 8000 : 3500);
    return () => clearTimeout(timer);
  }, [message, tone, onDone]);
  return <div className={`toast ${tone === 'error' ? 'error' : ''}`}>{message}</div>;
}

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    loader()
      .then((value) => {
        if (live) {
          setData(value);
          setError(null);
        }
      })
      .catch((err: Error) => live && setError(err.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload, setData };
}

export function Loading({ error }: { error?: string | null }) {
  if (error) return <div className="callout danger">{error}</div>;
  return <div className="muted">Loading…</div>;
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="callout info">{children}</div>;
}
